import { useEffect, useState } from "react";
import { RouterProvider } from "react-router-dom";
import { Center, Spinner } from "@chakra-ui/react";
import { useFlag, useFlagsStatus, useUnleashContext } from "@unleash/proxy-client-react";

import { routes } from "../../routes";
import { UnleashFlags } from "../../constants";
import { UnleashContextSync } from "./unleashContextSync";
import { UNLEASH_INITIAL_TIMEOUT_MS } from "./unleashClient";
import { usePersistStore } from "../../store/persistStore";
import { isActiveToken } from "../../utils/session/authorizationToken";
import { selectAuthorization } from "../../utils/session/clearAuthorization";
import { useAuthorizationStore, AuthorizationVersion } from "../../store/authorizationStore";

function AuthorizedRouter() {
  const [router] = useState(routes);
  const auth = useAuthorizationStore();
  const legacyToken = usePersistStore(state => state.deviceJWT ?? state.jwt);
  const hasToken = auth.version === "keycloak" ? isActiveToken(auth.token) : !!legacyToken;
  const updateContext = useUnleashContext();

  useEffect(() => {
    if (auth.version === "keycloak" && !auth.token) {
      void updateContext({ userId: undefined });
    }
  }, [auth.epoch]);

  return (
    <>
      {hasToken && <UnleashContextSync />}
      <RouterProvider router={router} />
    </>
  );
}

export function AuthorizationGate() {
  const status = useFlagsStatus();
  const flagsReady = status.flagsReady;
  const flagsError: unknown = status.flagsError;
  const newAuthorization = useFlag(UnleashFlags.newAuthorization);
  const foreignPassport = useFlag(UnleashFlags.foreignPassportAuthorization);

  const [selected, setSelected] = useState<AuthorizationVersion>();
  const [initialTimedOut, setInitialTimedOut] = useState(false);

  useEffect(() => {
    if (flagsReady || selected || flagsError || initialTimedOut) {
      return;
    }

    const timer = setTimeout(() => {
      setInitialTimedOut(true);
    }, UNLEASH_INITIAL_TIMEOUT_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [flagsReady, selected, flagsError, initialTimedOut]);

  useEffect(() => {
    if (flagsError && selected) return;
    if (!flagsReady && (selected || (!flagsError && !initialTimedOut))) {
      return;
    }

    // Without a successful initial fetch, ignore cached flags and choose Keycloak.
    const version = !flagsReady || newAuthorization ? "keycloak" : "legacy";
    const changed = selectAuthorization(version);
    useAuthorizationStore.setState({ foreignPassport: version === "keycloak" && foreignPassport });

    if (changed) {
      // A full navigation also discards legacy callbacks already running in this document.
      window.location.replace("/login");
      return;
    }

    setSelected(version);
  }, [flagsReady, flagsError, initialTimedOut, selected, newAuthorization, foreignPassport]);

  if (!selected || (flagsReady && selected !== (newAuthorization ? "keycloak" : "legacy"))) {
    return (
      <Center minH="100vh">
        <Spinner />
      </Center>
    );
  }

  return <AuthorizedRouter />;
}
